import { useMemo } from "react";
import { IdwTable } from "../../../../../common/idw-table/idw-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleDown } from "@fortawesome/free-solid-svg-icons/faChevronCircleDown";
import { resources } from "@constants/hardcoded-strings";
import { IdwTableContainer } from "../../../../../common/idw-table-container/idw-table-container";
import {
    FormattedVerificationTransactionDetailsTrustScoreInput,
    FormattedVerificationTransactionDetailsAssertionScoreBreakdown,
    FormattedVerificationTransactionDetailsAssertionScoreModifier
} from "@idw-types/data/verification-transaction-details";
import { VerificationTransactionScoreTypes } from "@enums/verification-transaction-score-types";
import styles from "./trust-score-inputs-table-container.less";
import { AssertionScoreDetailsTableContainer } from "./assertion-score-details-table-container/assertion-score-details-table-container";
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";

type TrustScoreInputsTableContainerProps = {
    trustScoreInputs: Array<FormattedVerificationTransactionDetailsTrustScoreInput>;
    assertionScoreBreakdown: FormattedVerificationTransactionDetailsAssertionScoreBreakdown;
    assertionScoreModifiers: Array<FormattedVerificationTransactionDetailsAssertionScoreModifier>;
};

type TrustScoreInputsTableDataRow = FormattedVerificationTransactionDetailsTrustScoreInput & {
    attributeTypeName: string;
    subRows: Array<any>;
}

export const TrustScoreInputsTableContainer = (props: TrustScoreInputsTableContainerProps) => {
    const { trustScoreInputs, assertionScoreBreakdown, assertionScoreModifiers } = props;

    const trustScoreInputsWithSubrows: Array<TrustScoreInputsTableDataRow> = useMemo(() => trustScoreInputs.map(trustScoreInput => {
        return {
            ...trustScoreInput,
            attributeTypeName: trustScoreInput.attributeType?.display || "",
            subRows: [{}]
        }
    }), [trustScoreInputs]);

    const columnHelper = createColumnHelper<TrustScoreInputsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.display({
            id: "expander",
            cell: (props) => {
                const { row } = props;
                if ([
                    VerificationTransactionScoreTypes.ASSERTION_SCORE,
                    VerificationTransactionScoreTypes.IDW_RISK_SCORE,
                    VerificationTransactionScoreTypes.IDW_TRUST_SCORE
                    //@ts-ignore
                ].includes(row.original.attributeType?.display)) {
                    return (
                        <button className={styles.chevronButton} id={`trust-score-input-expander-${row.index}`} onClick={row.getToggleExpandedHandler()}>
                            <FontAwesomeIcon className={`${styles.chevron} ${row.getIsExpanded() && styles.rotate}`} icon={faChevronCircleDown} />
                        </button>
                    )
                }
                return null;
            }
        }),
        columnHelper.accessor("attributeTypeName", {
            header: resources.tables.verificationTransactionDetailsScoreComponents.columns.scoreAttribute.header
        }),
        columnHelper.accessor("normalizedScore", {
            header: resources.tables.verificationTransactionDetailsScoreComponents.columns.normalizedScore.header
        })
    ]), [trustScoreInputsWithSubrows]);

    const useTableResult = useReactTable({
        columns,
        data: trustScoreInputsWithSubrows,
        filterFns: null,
        enableSorting: false,
        enableFilters: false,
        sortDescFirst: false,
        getRowCanExpand: () => true,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel()
    });

    const renderExpandedComponent = () => {
        const pointsEarned = assertionScoreBreakdown.rawAssertionScore;
        const totalPoints = assertionScoreBreakdown.assertionScoreRangeMax;
        return (
            <div className={styles.expandedTableRowContainer}>
                <div className={styles.expandableTableHeader}>
                    {resources.tables.assertionScoreDetails.title}
                    <span className={styles.totalPoints}>{resources.tables.assertionScoreDetails.totalPoints(pointsEarned, totalPoints)}</span>
                </div>
                <AssertionScoreDetailsTableContainer assertionScoreModifiers={assertionScoreModifiers} />
            </div>
        );
    };

    return (
        <IdwTableContainer className={styles.trustScoreInputsTableContainer}>
            <IdwTable
                id="trust-score-inputs"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"trust-score-inputs"}
                renderExpandedComponent={renderExpandedComponent}
                className={styles.trustScoreInputsTable}
            />
        </IdwTableContainer>
    );
};